import React from "react";

const LifeSciencesIndustryLayout = () => {
    return (
        <div className="bg-white">

            {/* ================= HERO ================= */}
            <section className="bg-[#2f2a35] text-white">
                <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="text-sm uppercase text-gray-300">Industries</span>
                        <h1 className="text-4xl md:text-5xl font-bold mt-4">
                            Life Sciences
                        </h1>
                        <p className="mt-6 text-gray-200 max-w-xl">
                            Creating world-class life sciences solutions through connected
                            digital platforms, intelligent automation, and data-driven
                            innovation.
                        </p>

                        <div className="mt-6 text-sm text-orange-400">
                            IDC Spotlight Sponsored by Persistent →
                        </div>
                    </div>

                    <div>
                        <img
                            src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b"
                            alt="Life Sciences"
                            className="rounded-xl shadow-lg"
                        />
                    </div>
                </div>
            </section>

            {/* ================= INTRO ================= */}
            <section className="py-20">
                <div className="max-w-5xl mx-auto px-6 text-gray-700 space-y-4">
                    <p>
                        Life sciences organizations are navigating rapid innovation,
                        regulatory complexity, and increasing expectations for faster,
                        safer, and more personalized outcomes.
                    </p>
                    <p>
                        We partner with pharmaceutical, biotech, CRO, and medical device
                        companies to modernize R&D, accelerate commercialization, and
                        improve operational excellence through digital transformation.
                    </p>
                </div>
            </section>

            {/* ================= INSTRUMENTS & DIAGNOSTICS ================= */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                        <h2 className="text-3xl font-bold mb-4">
                            Instruments & Diagnostics
                        </h2>
                        <p className="text-gray-700">
                            Enable next-generation diagnostics and connected instruments with
                            cloud platforms, digital engineering, and intelligent data
                            pipelines that drive faster insights and better patient outcomes.
                        </p>

                        <button className="mt-6 bg-orange-500 text-white px-6 py-3 rounded-md">
                            Learn more about our industry expertise
                        </button>
                    </div>

                    <div className="bg-[#020617] text-white rounded-xl p-6">
                        <h4 className="font-semibold mb-4">Our Offerings</h4>
                        <ul className="space-y-2 text-sm text-gray-300">
                            <li>• Digital Lab & Diagnostics Platforms</li>
                            <li>• Product Engineering & Validation</li>
                            <li>• Cloud & Infrastructure</li>
                            <li>• Data Analytics & AI</li>
                            <li>• Regulatory Compliance & Quality</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ================= RE(AI)MAGINING ================= */}
            <section
                className="py-24 text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(2,6,23,.85), rgba(2,6,23,.85)), url(https://images.unsplash.com/photo-1559757148-5c350d0d3c56)",
                    backgroundSize: "cover",
                }}
            >
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold">
                        Re(AI)magining™ Healthcare & Life Sciences
                    </h2>
                    <p className="mt-4 max-w-xl text-gray-200">
                        Applying AI-first strategies to accelerate discovery, optimize
                        clinical operations, and enable intelligent life sciences
                        ecosystems.
                    </p>
                </div>
            </section>

            {/* ================= CLIENT SUCCESS ================= */}
            <section className="py-20 bg-[#020617] text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">Client Success</h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                img: "https://images.unsplash.com/photo-1581091012184-7b8bafc23c6c",
                                tag: "Life Sciences Innovation",
                                title:
                                    "Life sciences company accelerates drug discovery using AI-powered research platforms",
                            },
                            {
                                img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d",
                                tag: "Biotech Research",
                                title:
                                    "Biotechnology firm improves laboratory efficiency through connected data ecosystems",
                            },
                            {
                                img: "https://images.unsplash.com/photo-1579154204601-01588f351e67",
                                tag: "Medical Technology",
                                title:
                                    "MedTech provider enhances patient diagnostics with cloud-enabled intelligent systems",
                            },
                        ].map((item, i) => (
                            <div key={i} className="bg-white text-black rounded-xl overflow-hidden">
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    className="w-full h-48 object-cover"
                                />

                                <div className="p-5">
                                    <span className="text-xs text-orange-600">{item.tag}</span>

                                    <p className="mt-2 font-medium">{item.title}</p>

                                    <button className="mt-3 text-orange-600 text-sm hover:underline">
                                        Read the story →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ================= WHY PERSISTENT ================= */}
            <section className="bg-[#fde7d9] py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
                    {[
                        [
                            "Deep Industry Expertise",
                            "Strong domain knowledge across life sciences value chains.",
                        ],
                        [
                            "Technology with Purpose",
                            "AI-driven platforms built for innovation and compliance.",
                        ],
                        [
                            "Partner Ecosystem",
                            "Strategic alliances enabling scale and global reach.",
                        ],
                    ].map(([title, desc]) => (
                        <div key={title}>
                            <h4 className="font-semibold mb-2">{title}</h4>
                            <p className="text-sm text-gray-700">{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================= NEWS ================= */}
            {/* <section className="py-20 bg-[#020617] text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">News & Insights</h2>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white text-black rounded-xl overflow-hidden">
                                <img
                                    src="https://images.unsplash.com/photo-1582719478170-2b6e1e1b55c4"
                                    alt="Life Sciences Insight"
                                />
                                <div className="p-5">
                                    <span className="text-xs text-orange-600">Blog</span>
                                    <p className="mt-2 font-medium">
                                        Advancing life sciences through AI-powered digital
                                        transformation
                                    </p>
                                    <button className="mt-3 text-orange-600 text-sm">
                                        Read the blog →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ================= SALESFORCE ================= */}
            <section
                className="py-24 text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(2,6,23,.9), rgba(2,6,23,.9)), url(https://images.unsplash.com/photo-1581090700227-1e37b190418e)",
                    backgroundSize: "cover",
                }}
            >
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold">
                        Salesforce for Healthcare & Life Sciences
                    </h2>
                    <p className="mt-4 max-w-xl text-gray-200">
                        Enable connected experiences across patients, providers, and
                        partners using Salesforce platforms tailored for life sciences.
                    </p>
                    <button className="mt-6 bg-white text-black px-6 py-3 rounded-md">
                        Learn more
                    </button>
                </div>
            </section>

            {/* ================= CONTACT ================= */}
            {/* <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-8">Contact us</h2>

                    <form className="grid md:grid-cols-2 gap-6">
                        <input className="border p-3 rounded" placeholder="First name" />
                        <input className="border p-3 rounded" placeholder="Last name" />
                        <input className="border p-3 rounded" placeholder="Work email" />
                        <input className="border p-3 rounded" placeholder="Company" />
                        <textarea
                            className="border p-3 rounded md:col-span-2"
                            rows="4"
                            placeholder="How can we help?"
                        />
                        <button className="bg-orange-500 text-white px-6 py-3 rounded-md">
                            Submit
                        </button>
                    </form>
                </div>
            </section> */}

        </div>
    );
};

export default LifeSciencesIndustryLayout;
